/**
 * Рядок із переповненням (components.md, розд. 12; stability.md, розд. 1, правило 11; аудит якості
 * 2026-10-27, № 11, 12, 15, 19, 20): один механізм для вкладок картки, чипів, тегів, дій панелі й
 * нижньої навігації. Що вміщується, рахує `fitRow` (overflow-row.ts); хук лише міряє й віддає розкладку.
 *
 * Як міряє:
 * - Контейнер — елемент з `ref`. Місце — його ширина без бічних полів. Елементи рядка позначені
 *   `data-ovf-item="<ключ>"`, кнопка «Ще» — `data-ovf-more`, сталі сусіди в тому ж контейнері
 *   («Усе», «+» тегів) — `data-ovf-fixed`.
 * - Елементи поза рядком лишаються в DOM поза потоком (`data-ovf-out`: `position: absolute`,
 *   `visibility: hidden`), тож їхні ширини відомі, а людині й програмі читання їх не видно.
 * - Геометрія (ширини) від вибору не залежить: розкладку рахує рендер із останнього заміру. Тому
 *   вибір з «Ще» стає в рядок у тому ж кадрі, без проміжного стану й без нового заміру.
 * - Вузли не переставляються, коли елемент іде в «Ще» й назад: сховані стоять у DOM на своїх місцях
 *   набору (`dom`), тож React їх не переносить — стан поля і фокус лишаються (рев'ю #902).
 * - Під наглядом `ResizeObserver` — контейнер і всі його поточні `[data-ovf-*]`: підписка звіряється
 *   після кожного коміту, тож і новий вузол (лічильник, чип з новим числом) дає новий замір. Свої
 *   вузли — без вузлів вкладеного рядка: той міряє себе сам.
 *
 * Без циклу вимірювання: замір — у колбеку `ResizeObserver` (після розкладки, до малювання), не
 * частіше одного проходу на кадр; той самий результат стану не міняє. Ширина контейнера не повинна
 * залежати від вмісту: інакше схований елемент звужує контейнер, і рядок «з'їдає» сам себе. Для
 * контейнера-елемента flex хук дає `style` з `flex-basis` (повна ширина всіх елементів), `min-width`
 * (закріплені, вибране й «Ще», якщо є що ховати) і `contain: inline-size`: внесок рядка в
 * `min-content` предка — лише цей `min-width`, а не показані елементи (інакше мінімум предка тримав
 * би їх у рядку, і в «Ще» вони не йшли б, QC #902). До першого заміру `style` порожній: рядок має
 * природну ширину, яку і міряємо.
 */
import {
  type CSSProperties,
  type RefObject,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import { fitRow } from './overflow-row.ts';

export interface OverflowRowOptions {
  /** Ключі елементів у порядку рядка. */
  keys: readonly string[];
  /** Вибраний: за межею стає в рядок останнім. */
  selected?: string | null | undefined;
  /** Завжди в рядку: основна дія, «Стрічка». */
  pinned?: readonly string[] | undefined;
  /** «Ще» потрібна й без надлишку (меню має власні пункти). */
  always?: boolean | undefined;
  /** Порядок, у якому елементи займають місце (перші — найважливіші); немає — порядок рядка. */
  priority?: readonly string[] | undefined;
}

/** Атрибути елемента рядка. */
export interface OverflowItemProps {
  'data-ovf-item': string;
  'data-ovf-out'?: '';
}

export interface OverflowRowLayout<T extends HTMLElement> {
  ref: RefObject<T | null>;
  /** Ключі в рядку, у порядку рядка (вибраний з «Ще» — останнім). */
  row: readonly string[];
  /** Ключі в «Ще», у порядку набору. */
  hidden: readonly string[];
  /**
   * Порядок у DOM: рядок, а сховані — на своїх місцях набору (їх видно лише заміру, поза потоком
   * вони не займають місця). Перехід у «Ще» й назад вузлів не переставляє.
   */
  dom: readonly string[];
  out: (key: string) => boolean;
  item: (key: string) => OverflowItemProps;
  /** Атрибути кнопки «Ще»: без надлишку вона теж поза рядком, але заміряна. */
  more: { 'data-ovf-more': ''; 'data-ovf-out'?: '' };
  /** Чи потрібна кнопка «Ще». */
  moreOn: boolean;
  /**
   * Сталі основа, мінімум і `contain: inline-size` контейнера-елемента flex (див. опис модуля); до
   * заміру — порожньо.
   */
  style: CSSProperties;
}

interface Geometry {
  /** Місце під елементи: без полів, рамок і сталих сусідів. */
  width: number;
  more: number;
  gap: number;
  /** Поля, рамки й сталі сусіди: різниця між повною шириною контейнера і `width`. */
  edges: number;
  widths: ReadonlyMap<string, number>;
}

const px = (value: string) => Number.parseFloat(value) || 0;

const PARTS = '[data-ovf-item], [data-ovf-more], [data-ovf-fixed]';

/** Вузли рядка `box` за селектором — без вузлів вкладеного рядка (вони всередині наших частин). */
export function own(box: HTMLElement, selector: string): HTMLElement[] {
  return Array.from(box.querySelectorAll<HTMLElement>(selector)).filter((node) => {
    for (let p = node.parentElement; p && p !== box; p = p.parentElement) {
      if (p.matches(PARTS) || p.hasAttribute('data-ovf-row')) return false;
    }
    return true;
  });
}

/**
 * Замір контейнера; `null` — ширин немає (прихований контейнер, jsdom без розкладки). Контейнер без
 * місця (сусіди стиснули рядок до 0 ще до першого заміру) — теж замір: інакше рядок без `style`
 * лишився б нульовим назавжди, а елементи — поза ним за краєм екрана (QC #902, панель на 600 px).
 */
function measure(box: HTMLElement): Geometry | null {
  const style = getComputedStyle(box);
  const room = box.clientWidth - px(style.paddingLeft) - px(style.paddingRight);
  const items = own(box, '[data-ovf-item]');
  const widths = new Map<string, number>();
  for (const node of items) widths.set(node.dataset.ovfItem ?? '', node.offsetWidth);
  if (room <= 0 && (box.getClientRects().length === 0 || ![...widths.values()].some((w) => w > 0)))
    return null;
  const first = items[0]?.parentElement;
  const gap = first ? px(getComputedStyle(first).columnGap) : 0;
  const moreNode = own(box, '[data-ovf-more]')[0];
  let more = 0;
  if (moreNode) {
    const s = getComputedStyle(moreNode);
    more = moreNode.offsetWidth + px(s.marginLeft) + px(s.marginRight);
  }
  let fixed = 0;
  for (const node of own(box, '[data-ovf-fixed]')) {
    if (node.offsetWidth === 0) continue;
    const parent = node.parentElement;
    fixed += node.offsetWidth + (parent ? px(getComputedStyle(parent).columnGap) : 0);
  }
  const width = Math.max(0, room - fixed);
  return { width, more, gap, edges: box.offsetWidth - (room - fixed), widths };
}

function same(a: Geometry | null, b: Geometry): boolean {
  if (!a || a.width !== b.width || a.more !== b.more || a.gap !== b.gap || a.edges !== b.edges) {
    return false;
  }
  if (a.widths.size !== b.widths.size) return false;
  for (const [key, w] of b.widths) if (a.widths.get(key) !== w) return false;
  return true;
}

export function useOverflowRow<T extends HTMLElement = HTMLDivElement>(
  options: OverflowRowOptions,
): OverflowRowLayout<T> {
  const { keys, selected = null, pinned, always = false, priority } = options;
  const ref = useRef<T>(null);
  const [geometry, setGeometry] = useState<Geometry | null>(null);
  const latest = useRef(geometry);
  latest.current = geometry;
  const observer = useRef<ResizeObserver | null>(null);
  const watched = useRef(new Set<Element>());
  const pass = useRef({ busy: false, again: false, frame: 0 });
  const signature = keys.join('\u0001');

  useLayoutEffect(
    () => () => {
      observer.current?.disconnect();
      observer.current = null;
      watched.current.clear();
      cancelAnimationFrame(pass.current.frame);
      pass.current = { busy: false, again: false, frame: 0 };
    },
    [],
  );

  // Після кожного коміту — під наглядом контейнер і його поточні вузли: новий вузол (лічильник,
  // перемонтована дія) дає новий замір, зниклий знімається. Звірка — лише з набором `watched`.
  useLayoutEffect(() => {
    const box = ref.current;
    if (!box || typeof ResizeObserver === 'undefined') return;
    if (!observer.current) {
      const p = pass.current;
      const run = () => {
        const host = ref.current;
        const next = host ? measure(host) : null;
        if (!next || same(latest.current, next)) return;
        latest.current = next;
        // Колбек приходить після розкладки, до малювання: рендеримо синхронно, без кадру-зсуву.
        flushSync(() => setGeometry(next));
        // Власна зміна може знову зачепити нагляд у цьому ж кадрі — наступний прохід лише в новому.
        p.busy = true;
        p.frame = requestAnimationFrame(() => {
          p.busy = false;
          p.frame = 0;
          if (!p.again) return;
          p.again = false;
          run();
        });
      };
      observer.current = new ResizeObserver(() => {
        if (p.busy) p.again = true;
        else run();
      });
    }
    const ro = observer.current;
    box.setAttribute('data-ovf-row', '');
    const now = new Set<Element>([box, ...own(box, PARTS)]);
    const seen = watched.current;
    for (const node of seen) {
      if (!now.has(node)) {
        ro.unobserve(node);
        seen.delete(node);
      }
    }
    for (const node of now) {
      if (!seen.has(node)) {
        ro.observe(node);
        seen.add(node);
      }
    }
  });

  const pinnedKey = pinned?.join('\u0001') ?? '';
  const priorityKey = priority?.join('\u0001') ?? '';
  // biome-ignore lint/correctness/useExhaustiveDependencies: ключі наборів — їхній вміст
  return useMemo(() => {
    const index = new Map(keys.map((k, i) => [k, i]));
    const pinnedSet = new Set(
      (pinned ?? []).flatMap((k) => (index.has(k) ? [index.get(k) as number] : [])),
    );
    let row: string[] = [...keys];
    if (geometry) {
      // Ще не заміряний елемент (щойно з'явився) місця не займає: замір виправить до малювання.
      const widths = keys.map((k) => geometry.widths.get(k) ?? 0);
      const fit = fitRow(widths, {
        width: geometry.width,
        more: geometry.more,
        gap: geometry.gap,
        pinned: pinnedSet,
        selected: selected === null ? -1 : (index.get(selected) ?? -1),
        always,
        ...(priority
          ? { priority: priority.flatMap((k) => (index.has(k) ? [index.get(k) as number] : [])) }
          : {}),
      });
      row = fit.flatMap((i) => (keys[i] === undefined ? [] : [keys[i]]));
    }
    const inRow = new Set(row);
    const hidden = keys.filter((k) => !inRow.has(k));
    // Сховані — на своїх місцях набору: перед першим елементом рядка, що стоїть у наборі далі.
    const dom: string[] = [];
    let h = 0;
    for (const key of row) {
      const at = index.get(key) ?? 0;
      while (h < hidden.length && (index.get(hidden[h] as string) ?? 0) < at) {
        dom.push(hidden[h] as string);
        h += 1;
      }
      dom.push(key);
    }
    dom.push(...hidden.slice(h));
    const moreOn = always || hidden.length > 0;
    let style: CSSProperties = {};
    if (geometry) {
      const sum = (list: readonly string[]) => {
        const shown = list.map((k) => geometry.widths.get(k) ?? 0).filter((w) => w > 0);
        return shown.reduce((s, w) => s + w, 0) + Math.max(0, shown.length - 1) * geometry.gap;
      };
      const { edges, more, gap } = geometry;
      // Мінімум рядка — закріплені, вибране (воно не ховається, `fitRow`) і «Ще»: без вибраного
      // рядок без закріплених (чипи подань) стискався б flex до полів і ховав навіть поточне (QC #902).
      const fixed = keys.filter((k) => pinnedSet.has(index.get(k) ?? -1) || k === selected);
      // «Ще» в мінімумі — лише коли є що ховати: рядок з одних закріплених (основна дія «Моїх задач»)
      // її ніколи не покаже, а зайві 40 px виштовхували панель за вікно на 360 px.
      const moreMin =
        always || fixed.length < keys.length ? more + (fixed.length > 0 ? gap : 0) : 0;
      style = {
        flexBasis: Math.ceil(sum(keys) + edges + (always ? more + gap : 0)),
        minWidth: Math.ceil(sum(fixed) + edges + moreMin),
        contain: 'inline-size',
      };
    }
    return {
      ref,
      row,
      hidden,
      dom,
      out: (key: string) => !inRow.has(key),
      item: (key: string) =>
        inRow.has(key) ? { 'data-ovf-item': key } : { 'data-ovf-item': key, 'data-ovf-out': '' },
      more: moreOn ? { 'data-ovf-more': '' } : { 'data-ovf-more': '', 'data-ovf-out': '' },
      moreOn,
      style,
    } satisfies OverflowRowLayout<T>;
  }, [geometry, signature, selected, pinnedKey, priorityKey, always]);
}
